import {
	navigateTo,
	createUrlRelativeToEnvironment
} from "client_legacy/ndaw/navigator";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { ExchangeServerInstanceType } from "client/Api/Email/ExchangeSettings/environmentExchangeSettings";
import {
	navigateToEmailInOwaOnline,
	navigateToComposeEmailInOwa,
	navigateToComposeEmailInOwaOnPremises,
	navigateToOwa
} from "client/Features/Email/OwaNavigation/owaNavigator";
import { openEmailComposerDialog } from "client/Features/Email/Dialogs/EmailComposerDialog";
import { DestinationWindow } from "client/Api/Apps/common";
import { EmailClientPreference } from "../ndaw/workspaceAbilitiesStatusProvider";

interface IEmailClientFeatures {
	supportsEmailCompose: boolean;
	supportsEmailComposeWithBody: boolean;
	supportsOverview: boolean;
	supportsEditDraft: boolean;
	supportsNavigateToEmail: boolean;
}

interface IEmailClientComposeFeature {
	supportsEmailCompose: true;
	composeEmail(options?: { to?: string | nil; }): void;
}

interface IEmailClientComposeWithBodyFeature {
	supportsEmailComposeWithBody: true;
	composeEmailWithBody(options?: { to?: string | nil; body?: string; }): void;
}

interface IEmailClientOverviewFeature {
	supportsOverview: true;
	isExternal: boolean;
	openOverview(options?: { inNewWindow?: boolean; folderId?: string; }): void;
}

type NavigateToEmailFeatureOptions = {
	inNewWindow?: boolean;
	emailId: string;
	mailbox?: string | null;
};

interface IEmailClientNavigateToEmailFeature {
	supportsNavigateToEmail: true;
	navigateToEmail(options: NavigateToEmailFeatureOptions): void;
}

interface IEmailClientEditDraftFeature {
	supportsEditDraft: true;
	editDraft(options: { draftId: string; representsANewlyGeneratedDraft?: boolean; }): void;
}

class DisabledEmailClient implements IEmailClientFeatures {
	public get supportsEmailCompose(): false { return false; }
	public get supportsEmailComposeWithBody(): false { return false; }
	public get supportsOverview(): false { return false; }
	public get supportsEditDraft(): false { return false; }
	public readonly supportsNavigateToEmail: false = false;
}

class MailToEmailClient implements IEmailClientComposeFeature, IEmailClientComposeWithBodyFeature, IEmailClientFeatures {
	public get supportsEmailCompose(): true { return true; }
	public get supportsEmailComposeWithBody(): true { return true; }
	public get supportsOverview(): false { return false; }
	public get supportsEditDraft(): false { return false; }
	public readonly supportsNavigateToEmail: false = false;

	public composeEmail(options?: {to?: string | nil;}) {
		this.composeEmailWithBody(options);
	}

	public composeEmailWithBody({ to, body }: {to?: string | nil; body?: string;} = {}) {
		if (body) {
			navigateTo(`mailto:${to||""}?body=${encodeURIComponent(body)}`);
		} else {
			navigateTo(`mailto:${to||""}`);
		}
	}
}

class OwaOnPremisesEmailClient implements
	IEmailClientComposeFeature,
	IEmailClientOverviewFeature,
	IEmailClientFeatures,
	IEmailClientNavigateToEmailFeature
{
	private readonly _shouldOpenInNewWindowByDefault: boolean;
	public get supportsEmailCompose(): true { return true; }
	public get supportsOverview(): true { return true; }
	public get supportsEmailComposeWithBody(): false { return false; }
	public get supportsEditDraft(): false { return false; }
	public readonly supportsNavigateToEmail: true = true;
	public readonly isExternal: true = true;

	constructor(
		shouldOpenInNewWindowByDefault: boolean
	) {
		this._shouldOpenInNewWindowByDefault = shouldOpenInNewWindowByDefault;
	}

	public composeEmail(_options?: { to?: string | nil; }) {
		// On premises Exchange 2013 doesn't support passing any parameters into it's email composer so we cant specify email recipient in any way
		// I do not have on premises Exchange 2016 to test, so let's take the safer way and assume it's not working there as well, so only Office 365 has it
		navigateToComposeEmailInOwaOnPremises();
	}

	public openOverview(options: { inNewWindow?: boolean; folderId?: string; }) {
		navigateToOwa({
			inNewWindow: options.inNewWindow ?? this._shouldOpenInNewWindowByDefault
		});
	}

	public navigateToEmail(options: NavigateToEmailFeatureOptions) {
		navigateToOwa({
			inNewWindow: options.inNewWindow ?? this._shouldOpenInNewWindowByDefault
		});
	}
}

class OwaOnlineEmailClient implements
	IEmailClientComposeFeature,
	IEmailClientComposeWithBodyFeature,
	IEmailClientOverviewFeature,
	IEmailClientFeatures,
	IEmailClientNavigateToEmailFeature
{
	private readonly _shouldOpenInNewWindowByDefault: boolean;
	public get supportsEmailCompose(): true { return true; }
	public get supportsEmailComposeWithBody(): true { return true; }
	public get supportsOverview(): true { return true; }
	public get supportsEditDraft(): false { return false; }
	public readonly supportsNavigateToEmail: true = true;
	public readonly isExternal: true = true;

	constructor(
		shouldOpenInNewWindowByDefault: boolean
	) {
		this._shouldOpenInNewWindowByDefault = shouldOpenInNewWindowByDefault;
	}

	public composeEmail(options?: { to?: string | nil; }) {
		this.composeEmailWithBody(options);
	}

	public composeEmailWithBody(options?: { to?: string | nil; body?: string; }) {
		navigateToComposeEmailInOwa(options);
	}

	public openOverview(options: { inNewWindow?: boolean; folderId?: string; }) {
		navigateToOwa({
			inNewWindow: options.inNewWindow ?? this._shouldOpenInNewWindowByDefault
		});
	}

	public navigateToEmail(options: NavigateToEmailFeatureOptions) {
		navigateToEmailInOwaOnline(
			options.emailId,
			options.inNewWindow ?? this._shouldOpenInNewWindowByDefault
		);
	}
}

type NavigateToEmailOptions = {
	inNewWindow: boolean;
	mailbox?: string | null;
} & ({
	emailId: string;
} | {
	folderId?: string;
});

class WorkspaceEmailClient implements
	IEmailClientComposeFeature,
	IEmailClientOverviewFeature,
	IEmailClientEditDraftFeature,
	IEmailClientFeatures,
	IEmailClientNavigateToEmailFeature
{
	private readonly _shouldOpenInNewWindowByDefault: boolean;
	public get supportsEmailCompose(): true { return true; }
	public get supportsEmailComposeWithBody(): false { return false; }
	public get supportsOverview(): true { return true; }
	public get supportsEditDraft(): true { return true; }
	public readonly supportsNavigateToEmail: true = true;
	public readonly isExternal: false = false;

	constructor(
		shouldOpenInNewWindowByDefault: boolean
	) {
		this._shouldOpenInNewWindowByDefault = shouldOpenInNewWindowByDefault;
	}

	public composeEmail({ to }: { to?: string | nil; } = {}) {
		openEmailComposerDialog({
			isNew: true,
			to: to
		});
	}

	public openOverview(options: { inNewWindow?: boolean; folderId?: string; mailbox?: string | null; }) {
		const {
			inNewWindow,
			...otherOptions
		} = options;

		this.navigateToEmailInternal({
			...otherOptions,
			inNewWindow: inNewWindow ?? this._shouldOpenInNewWindowByDefault
		});
	}

	public navigateToEmail(options: NavigateToEmailFeatureOptions) {
		const {
			inNewWindow,
			...otherOptions
		} = options;

		this.navigateToEmailInternal({
			...otherOptions,
			inNewWindow: inNewWindow ?? this._shouldOpenInNewWindowByDefault
		});
	}

	public editDraft(options: { draftId: string; representsANewlyGeneratedDraft?: boolean; }) {
		openEmailComposerDialog({
			isNew: false,
			draftId: options.draftId,
			doesRepresentNewlyGeneratedDraft: options.representsANewlyGeneratedDraft ?? false
		});
	}

	private navigateToEmailInternal(options: NavigateToEmailOptions): void {
		const queryStringParameters: string[] = [];

		if ("emailId" in options && options.emailId) {
			queryStringParameters.push(`emailToOpen=${encodeURIComponent(options.emailId)}`);
		}

		if (options.mailbox) {
			queryStringParameters.push(`mailboxToOpen=${encodeURIComponent(options.mailbox)}`);
		}

		if ("folderId" in options && options.folderId) {
			queryStringParameters.push(`folderToOpen=${encodeURIComponent(options.folderId)}`);
		}

		let relativeUrl: string = "Email/";

		if (queryStringParameters.length > 0) {
			relativeUrl += "?" + queryStringParameters.join("&");
		}

		const url: string = createUrlRelativeToEnvironment(relativeUrl);

		navigateTo(url, options.inNewWindow);
	}
}

type OwaEmailClient = OwaOnlineEmailClient | OwaOnPremisesEmailClient;
type EmailClient = DisabledEmailClient | MailToEmailClient | OwaEmailClient | WorkspaceEmailClient;

function getEffectiveOwaEmailClient(shouldOpenInNewWindowByDefault: boolean): OwaEmailClient {
	const { exchange } = userConfiguration();
	if (exchange.exchangeServerInstanceType === ExchangeServerInstanceType.Office365) {
		return new OwaOnlineEmailClient(shouldOpenInNewWindowByDefault);
	} else {
		return new OwaOnPremisesEmailClient(shouldOpenInNewWindowByDefault);
	}
}

function getEffectiveEmailClient(): EmailClient {
	const { email, exchange } = userConfiguration();
	if (!exchange.isEnabled || !exchange.isConfigured) {
		return new DisabledEmailClient();
	}
	switch (email.emailClientPreference) {
		case EmailClientPreference.UseNone:
			return new MailToEmailClient();
		case EmailClientPreference.Workspace:
			return new WorkspaceEmailClient(email.destinationWindow === DestinationWindow.NewWindow);
		case EmailClientPreference.OutlookWebApp:
			return getEffectiveOwaEmailClient(email.destinationWindow === DestinationWindow.NewWindow);
		default:
			throw new UnreachableCaseError(email.emailClientPreference);
	}
}

export {
	type EmailClient,
	MailToEmailClient,
	type OwaEmailClient,
	WorkspaceEmailClient,
	type IEmailClientComposeFeature,
	type IEmailClientEditDraftFeature,
	type IEmailClientComposeWithBodyFeature,
	getEffectiveEmailClient,
	getEffectiveOwaEmailClient
};
